/**
 * Collaboration Decline Utilities
 * 
 * Template text and detection logic for declining collaboration requests.
 */

// Default decline templates
export const DECLINE_TEMPLATES = {
    default: "Thanks so much for reaching out about collaborating. At the moment it's not the right fit for us, but I appreciate the thought. Let's stay in touch and revisit in the future.",
    short: "Thank you for reaching out.  Currently, this partnership is not a good fit for me.  I wish you the best in your search!"
};

// Keywords that indicate a message may be a collaboration request
const COLLAB_KEYWORDS = [
    'collab',
    'collaborat', // matches collaborate, collaboration, collaborating
    'partnership',
    'partner with',
    'work together',
    'work with you',
    'sponsorship',
    'sponsor',
    'campaign',
    'opportunity',
    'interested in working',
    'would love to work',
    'looking to work',
    'content creation',
    'influencer',
    'creator collab',
    'host collab',
    'stay at your',
    'feature your',
    'promote your',
    'promote our',
    'cross-promotion',
    'brand deal',
    'paid partnership'
];

// Phrases that indicate a DECLINE or RESPONSE message (should NOT trigger the "Decline Collaboration?" button)
// This includes both decline phrases AND acceptance phrases - we don't want to show "Decline?" on either
const DECLINE_PHRASES = [
    // Decline phrases
    'going to pass',
    'not the right fit',
    'pass for now',
    'not a fit',
    'decline',
    'appreciate the thought',
    'appreciate it',
    'not interested',
    'can\'t pursue',
    'unable to',
    'won\'t be able to',
    // Acceptance phrases - also should NOT show decline button
    'accepted the collaboration',
    'i\'ve accepted',
    'i have accepted',
    'accept the collaboration',
    'accepted your request',
    'accepted your collaboration',
    'let\'s get started',
    'excited to work',
    'looking forward to working',
    'can\'t wait to work',
    'happy to collaborate',
    'love to collaborate',
    'accepted!'
];

/**
 * Check if a message text appears to be a collaboration request.
 * Uses lightweight keyword matching (case-insensitive).
 * Also excludes messages that look like decline responses.
 * 
 * @param {string} text - The message text to analyze
 * @returns {boolean} - True if the message likely contains a collab request (and is NOT a decline)
 */
export const hasCollabIntent = (text) => {
    if (!text || typeof text !== 'string') return false;
    
    const lowerText = text.toLowerCase();
    
    // Check if this looks like a decline message - if so, it's NOT a collab request
    const looksLikeDecline = DECLINE_PHRASES.some(phrase => lowerText.includes(phrase));
    if (looksLikeDecline) {
        return false;
    }
    
    return COLLAB_KEYWORDS.some(keyword => lowerText.includes(keyword));
};

/**
 * Get the default decline template text.
 * 
 * @param {string} variant - Template variant: 'default' or 'short'
 * @returns {string} - The template text
 */
export const getDeclineTemplate = (variant = 'default') => {
    return DECLINE_TEMPLATES[variant] || DECLINE_TEMPLATES.default;
};
